import type { Correction, CorrectionStudentWorkDetail } from '@kimuno/api-sdk';
import immutable from 'object-path-immutable';

import { LOGOUT, type LogoutAction } from '../actions/auth.actionTypes';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import {
	ADD_STUDENT_WORK,
	type CorrectionsActionTypes,
	CREATE_CORRECTION,
	EVALUATE_CORRECTION,
	GET_CORRECTION,
	GET_STUDENT_WORK,
	REMOVE_STUDENT_WORK,
	RENAME_STUDENT_WORK,
	UPDATE_CORRECTION,
	UPDATE_CORRECTION_TITLE,
	UPDATE_STUDENT_WORK,
} from '../actions/corrections.actionTypes';
import type { StoreItem } from '.';

export interface CorrectionsState {
	byId: Record<string, StoreItem<Correction>>;
	// The full result of one work, keyed by work id; the correction under `byId`
	// carries only each work's summary.
	studentWorksById: Record<string, StoreItem<CorrectionStudentWorkDetail>>;
	// Removing a work leaves the correction's updatedAt alone, so a poll sent
	// before the removal and landing after it passes the updatedAt guard with the
	// work still in it; every later snapshot drops the ids removed in this session.
	removedWorkIds: Record<string, true>;
}

const initialState: CorrectionsState = { byId: {}, studentWorksById: {}, removedWorkIds: {} };

const withoutRemovedWorks = (
	removedWorkIds: CorrectionsState['removedWorkIds'],
	snapshot: Correction
): Correction =>
	snapshot.studentWorks.some((work) => removedWorkIds[work.id])
		? {
				...snapshot,
				studentWorks: snapshot.studentWorks.filter((work) => !removedWorkIds[work.id]),
			}
		: snapshot;

// Starting a run leaves the correction's updatedAt alone, so a poll that read the
// data just before a start can land after the start's answer and pass the
// updatedAt guard. A run is identified by its startedAt: a missing or older run
// in the poll never replaces the stored one.
const keepStartedEvaluations = (stored: Correction | undefined, polled: Correction): Correction => {
	if (!stored) {
		return polled;
	}
	const storedEvaluations = new Map(stored.studentWorks.map((work) => [work.id, work.evaluation]));
	let hasKept = false;
	const studentWorks = polled.studentWorks.map((work) => {
		const storedEvaluation = storedEvaluations.get(work.id);
		if (storedEvaluation && (work.evaluation?.startedAt ?? 0) < storedEvaluation.startedAt) {
			hasKept = true;
			return { ...work, evaluation: storedEvaluation };
		}
		return work;
	});
	return hasKept ? { ...polled, studentWorks } : polled;
};

// A re-fetch keeps the previous data visible while LOADING, so a poll tick never
// blanks the screen; only the first fetch of an id has no data to keep.
const corrections = (
	// biome-ignore lint/style/useDefaultParameterLast: parameter order is a fixed contract (Redux reducer signature / existing call sites)
	state = initialState,
	action: CorrectionsActionTypes | LogoutAction
): CorrectionsState => {
	switch (action.type) {
		case LOGOUT.SUCCESS:
			return initialState;

		case CREATE_CORRECTION.SUCCESS:
			return immutable(state)
				.set(['byId', action.result.id], { data: action.result, status: AsyncActionStatus.READY })
				.value();

		case GET_CORRECTION.START:
		case UPDATE_CORRECTION.START:
		case UPDATE_CORRECTION_TITLE.START:
		case ADD_STUDENT_WORK.START:
		case REMOVE_STUDENT_WORK.START:
		case RENAME_STUDENT_WORK.START:
		case EVALUATE_CORRECTION.START:
			return immutable(state)
				.set(['byId', action.payload.correctionId, 'status'], AsyncActionStatus.LOADING)
				.value();

		case GET_CORRECTION.SUCCESS:
		case UPDATE_CORRECTION.SUCCESS:
		case UPDATE_CORRECTION_TITLE.SUCCESS:
		case ADD_STUDENT_WORK.SUCCESS:
		case REMOVE_STUDENT_WORK.SUCCESS:
		case RENAME_STUDENT_WORK.SUCCESS:
		case EVALUATE_CORRECTION.SUCCESS: {
			// Each answer is a full snapshot, and a poll or a title save can overlap another
			// request: a snapshot older than the stored one must not undo the newer write.
			const removedWorkIds =
				action.type === REMOVE_STUDENT_WORK.SUCCESS
					? { ...state.removedWorkIds, [action.payload.studentWorkId]: true as const }
					: state.removedWorkIds;
			const snapshot = withoutRemovedWorks(removedWorkIds, action.result);
			const stored = state.byId[action.payload.correctionId]?.data;
			const data =
				stored && snapshot.updatedAt < stored.updatedAt
					? withoutRemovedWorks(removedWorkIds, stored)
					: action.type === GET_CORRECTION.SUCCESS
						? keepStartedEvaluations(stored, snapshot)
						: snapshot;

			return immutable(state)
				.set(['byId', action.payload.correctionId], { data, status: AsyncActionStatus.READY })
				.set(['removedWorkIds'], removedWorkIds)
				.value();
		}

		case GET_CORRECTION.ERROR:
		case UPDATE_CORRECTION.ERROR:
		case UPDATE_CORRECTION_TITLE.ERROR:
		case ADD_STUDENT_WORK.ERROR:
		case REMOVE_STUDENT_WORK.ERROR:
		case RENAME_STUDENT_WORK.ERROR:
		case EVALUATE_CORRECTION.ERROR:
			return immutable(state)
				.set(['byId', action.payload.correctionId, 'status'], AsyncActionStatus.ERROR)
				.set(['byId', action.payload.correctionId, 'error'], action.error.message)
				.value();

		case GET_STUDENT_WORK.START:
			return immutable(state)
				.set(['studentWorksById', action.payload.studentWorkId, 'status'], AsyncActionStatus.LOADING)
				.value();

		// The update has no START / ERROR cases on purpose: a LOADING or ERROR status
		// here would make `useStudentWorkDetail` treat the stored result as failed and
		// the page would hide it. The card reports a failed save itself.
		case GET_STUDENT_WORK.SUCCESS:
		case UPDATE_STUDENT_WORK.SUCCESS:
			return immutable(state)
				.set(['studentWorksById', action.payload.studentWorkId], {
					data: action.result,
					status: AsyncActionStatus.READY,
				})
				.value();

		case GET_STUDENT_WORK.ERROR:
			return immutable(state)
				.set(['studentWorksById', action.payload.studentWorkId, 'status'], AsyncActionStatus.ERROR)
				.set(['studentWorksById', action.payload.studentWorkId, 'error'], action.error.message)
				.value();

		default:
			return state;
	}
};

export default corrections;
